import { useMemo } from "react";
import {
  BadgeCheck,
  Building2,
  CalendarDays,
  CheckCircle2,
  Circle,
  Landmark,
  Phone,
  ShieldCheck,
  Star,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  useMerchantReviews,
  useSingleUser,
  useUserReputationScore,
} from "@/hooks";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/store/hooks";

import {
  setAmount,
  setCurrentPage,
  setPaymentMethodFilter,
  setSearchQuery,
  setSelectedAd,
  setSide,
  setCryptoCode,
} from "@/store/slices/p2pMarketSlice";
import { TradeType } from "@/interfaces/trade";
import { getActivationDate } from "@/lib/utils";

interface MerchantHeaderProps {
  merchantId: string;
  onViewAds: () => void;
}

export default function MerchantHeader({
  merchantId,
  onViewAds,
}: MerchantHeaderProps) {
  const { data: merchant } = useSingleUser(merchantId);
  const { data: merchantReview } = useMerchantReviews(merchantId);

  const totalReviews = merchantReview?.summary?.totalReviews ?? 0;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { data: userScore } = useUserReputationScore(merchant?.id || "");
  console.log("USER SCORE", userScore);

  const hadleNavigate = () => {
    dispatch(setSearchQuery(merchant?.merchantName || ""));
    dispatch(setCurrentPage(1));
    dispatch(setPaymentMethodFilter(""));
    dispatch(setCryptoCode(""));
    dispatch(setSelectedAd(null));
    dispatch(setAmount(""));

    dispatch(setSide(TradeType.BUY));
    navigate("/p2p");
  };

  const initials = useMemo(() => {
    if (!merchant?.merchantName) return "M";

    return merchant.merchantName
      .split(" ")
      .map((x: string) => x[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }, [merchant]);

  return (
    <Card className="rounded-3xl border shadow-sm overflow-hidden">
      <div className="h-2 w-full bg-gradient-to-r from-primary via-primary/70 to-primary/30" />
      <div className="p-6 md:p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          {/* LEFT */}

          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar */}

            <Avatar className="h-24 w-24 border">
              <AvatarImage src={merchant?.avatar} />

              <AvatarFallback className="text-2xl font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>

            {/* Merchant Info */}

            <div className="space-y-5">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-3xl font-bold tracking-tight">
                    {merchant?.merchantName || merchant?.username}
                  </h1>

                  {merchant?.emailVerified && (
                    <Badge className="rounded-full">
                      <BadgeCheck className="mr-1 h-4 w-4" />
                      Verified Merchant
                    </Badge>
                  )}

                  {merchant?.lastSeen && (
                    <Badge variant="secondary" className="rounded-full">
                      <Circle className="mr-2 h-2 w-2 fill-green-500 text-green-500" />
                      Online
                    </Badge>
                  )}
                </div>

                <p className="text-muted-foreground mt-2">
                  @{merchant?.merchantName || merchant?.username}
                </p>
              </div>

              {/* Rating */}

              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />

                  <span className="font-semibold">
                    {userScore?.reputationScore}
                  </span>

                  <span className="text-muted-foreground">
                    ({totalReviews} {totalReviews === 1 ? "Review" : "Reviews"})
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />

                  <span className="font-semibold">
                    {userScore?.completionRate}%
                  </span>

                  <span className="text-muted-foreground">Completion Rate</span>
                </div>

                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-primary" />

                  <span className="font-semibold">
                    {userScore?.completedTrades}
                  </span>

                  <span className="text-muted-foreground">Trades</span>
                </div>

                <div className="flex items-center gap-2">
                  <CalendarDays className="h-5 w-5" />

                  <span className="text-muted-foreground">Member since</span>

                  <span className="font-semibold">
                    {getActivationDate(merchant?.createdAt)}
                  </span>
                </div>
              </div>

              {/* Verification */}

              <div className="flex flex-wrap gap-2">
                {merchant?.kycStatus && (
                  <Badge variant="outline">
                    <BadgeCheck className="mr-1 h-4 w-4" />
                    ID Verified
                  </Badge>
                )}

                {merchant?.phoneVerified && (
                  <Badge variant="outline">
                    <Phone className="mr-1 h-4 w-4" />
                    Phone
                  </Badge>
                )}

                {merchant?.kycStatus && (
                  <Badge variant="outline">
                    <Building2 className="mr-1 h-4 w-4" />
                    Merchant
                  </Badge>
                )}

                {merchant?.paymentMethods &&
                  merchant?.paymentMethods?.length > 0 && (
                    <Badge variant="outline">
                      <Landmark className="mr-1 h-4 w-4" />
                      Bank
                    </Badge>
                  )}
              </div>
            </div>
          </div>

          {/* RIGHT */}

          <div className="flex flex-col gap-3 w-full lg:w-auto">
            <Button
              onClick={hadleNavigate}
              size="lg"
              className="rounded-xl min-w-[220px]"
            >
              Trade With Merchant
            </Button>

            <Button
              onClick={onViewAds}
              size="lg"
              variant="outline"
              className="rounded-xl"
            >
              View Active Ads
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
